import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useSearchParams, useRouter } from 'src/routes/hooks';
// config
import { PATH_AFTER_LOGIN } from 'src/config-global';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';
import { postFetcher } from 'src/utils/axios';
import { useNavigate } from 'react-router';
// utils

// ----------------------------------------------------------------------

export default function JwtHeightLoginView() {
  const { thelogin, login, user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const [errorMsg, setErrorMsg] = useState('');
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const password = useBoolean();
  const newpassword = useBoolean();
  const confirmnewpassword = useBoolean();
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const getUserRole = () => {
    if (user?.is_admin) return 'admin';
    if (user?.is_academic) return 'academic';
    if (user?.is_non_academic) return 'non_academic';
    if (user?.is_student) return 'student';
    if (user?.is_parent) return 'parent';
    if (user?.is_height_student) return 'height_student';
    if (user?.is_outsider) return 'outsider';
    return null;
  };

  const role = getUserRole();

  const navigate = useNavigate();

  const handleSignInClick = () => {
    navigate('/auth/jwt/login');
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Username or Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const ForgotPasswordSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    newPassword: Yup.string().required('New password is required').min(6, 'Password must be at least 6 characters'),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const forgotPasswordDefaultValues = {
    username: '',
    newPassword: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const forgotPasswordMethods = useForm({
    resolver: yupResolver(ForgotPasswordSchema),
    defaultValues: forgotPasswordDefaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const {
    handleSubmit: handleForgotPasswordSubmit,
    formState: { isValid },
    watch,
    getValues,
  } = forgotPasswordMethods;

  const { username } = watch(); // Watch the username field for real-time validation

  const PATH =
    user &&
    (role === 'height_student'
      ? paths.heightstudentdashboard.root
      : role === 'student'
      ? paths.studentdashboard.root
      : paths.dashboard.user.list);

  useEffect(() => {
    if (role) {
      if (role === 'height_student') {
        router.push(paths.heightstudentdashboard.root);
      } else if (role === 'student') {
        router.push(paths.studentdashboard.root);
      } else {
        router.push(paths.dashboard.user.list);
      }
    }
  }, [role, router]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await thelogin?.(data.email, data.password);
      sessionStorage.setItem('type', 'thelogin');

    } catch (error) {
      console.error(error);
      reset();
      setErrorMsg(typeof error === 'string' ? error : error.message);
      enqueueSnackbar('Invalid', { variant: 'error' });
    }
  });

  const handleOpenForgotPassword = () => {
    setOpenForgotPassword(true);
  };

  const handleCloseForgotPassword = () => {
    setOpenForgotPassword(false);
    setOtp(['', '', '', '', '', '']);
    setOtpSent(false);
    setOtpVerified(false);
    setIsSendingOtp(false);
    setIsVerifyingOtp(false);
    setIsResettingPassword(false);
    forgotPasswordMethods.reset();
  };

  const handleOtpChange = (index, value, event) => {
    if (event.key === 'Backspace' && !value && index > 0) {
      // Clear current field and move to previous
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
      document.getElementById(`otp-${index - 1}`).focus();
    } else if (/^[0-9]?$/.test(value)) {
      // Update OTP value and move to next field if a digit is entered
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`).focus();
      }
    }
  };

  const handleSendOtp = async () => {
    setIsSendingOtp(true);
    try {
      const response = await postFetcher([
        '/accounts/api/forgot-password/request-otp/',
        { username: getValues('username') },
      ]);
      setOtpSent(true);
      enqueueSnackbar('OTP sent to your email', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || 'Failed to send OTP', { variant: 'error' });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    setIsVerifyingOtp(true);
    const otpCode = otp.join('');
    if (otpCode.length !== 6 || !/^\d{6}$/.test(otpCode)) {
      enqueueSnackbar('Please enter a valid 6-digit OTP', { variant: 'error' });
      setIsVerifyingOtp(false);
      return;
    }
    try {
      const response = await postFetcher([
        '/accounts/api/forgot-password/verify-otp/',
        { username: getValues('username'), otp: otpCode },
      ]);
      setOtpVerified(true);
      enqueueSnackbar('OTP verified successfully', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || 'Invalid OTP', { variant: 'error' });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const onForgotPasswordSubmit = handleForgotPasswordSubmit(async (data) => {
    setIsResettingPassword(true);
    try {
      const response = await postFetcher([
        '/accounts/api/forgot-password/reset/',
        { username: data.username, new_password: data.newPassword, confirm_password: data.confirmPassword },


      ]);
      enqueueSnackbar('Password reset successfully', { variant: 'success' });
      handleCloseForgotPassword();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || 'Failed to reset password', { variant: 'error' });
    } finally {
      setIsResettingPassword(false);
    }
  });

  const renderHead = (
    <Stack spacing={2} sx={{ mb: 5 }}>
      <Typography variant="h4">Sign in to Talent Hunt</Typography>
    </Stack>
  );

  const renderForm = (
    <Stack spacing={2.5}>
      {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}

      <RHFTextField name="email" label="Username or Email" placeholder="Enter 8-digit username or email" />

      <RHFTextField
        name="password"
        label="Password"
        type={password.value ? 'text' : 'password'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Link
        variant="body2"
        color="inherit"
        underline="always"
        sx={{ alignSelf: 'flex-end', cursor: 'pointer' }}
        onClick={handleOpenForgotPassword}
      >
        Forgot password?
      </Link>
      {/* <Link
        variant="body2"
        color="inherit"
        underline="always"
        sx={{ alignSelf: 'flex-end', cursor: 'pointer' }}
        onClick={handleSignInClick}

      >
        Sign In to Helix Portal
      </Link> */}
      <LoadingButton
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        Login
      </LoadingButton>
    </Stack>
  );

  const renderForgotPasswordDialog = (
    <Dialog maxWidth="xs" fullWidth open={openForgotPassword} onClose={handleCloseForgotPassword}>
      <FormProvider methods={forgotPasswordMethods} onSubmit={onForgotPasswordSubmit}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {!otpSent ? (
              <>
                <RHFTextField
                  name="username"
                  label="Enter Username"
                />
                <LoadingButton
                  variant="contained"
                  onClick={handleSendOtp}
                  disabled={!username}
                  loading={isSendingOtp}
                >
                  Send OTP
                </LoadingButton>
              </>
            ) : !otpVerified ? (
              <>
                <Typography variant="body2">Enter the 6-digit OTP sent to {username}</Typography>
                <Box display="flex" gap={1}>
                  {otp.map((digit, index) => (
                    <TextField
                      key={index}
                      id={`otp-${index}`}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value, e)}
                      onKeyDown={(e) => handleOtpChange(index, digit, e)}
                      inputProps={{
                        maxLength: 1,
                        style: { textAlign: 'center' },
                      }}
                      sx={{ width: 50 }}
                    />
                  ))}
                </Box>
                <LoadingButton
                  variant="contained"
                  onClick={handleVerifyOtp}
                  loading={isVerifyingOtp}
                >
                  Verify OTP
                </LoadingButton>
              </>
            ) : (
              <>
                <Typography variant="body2">Enter your new password</Typography>
                <RHFTextField
                  name="newPassword"
                  label="New Password"
                  type={newpassword.value ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={newpassword.onToggle} edge="end">
                          <Iconify icon={newpassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <RHFTextField
                  name="confirmPassword"
                  label="Confirm New Password"
                  type={confirmnewpassword.value ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={confirmnewpassword.onToggle} edge="end">
                          <Iconify icon={confirmnewpassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForgotPassword}>Cancel</Button>
          {otpVerified && (
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isResettingPassword}
            >
              Save
            </LoadingButton>
          )}
        </DialogActions>
      </FormProvider>
    </Dialog>
  );

  return (
    <>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        {renderHead}
        {renderForm}
      </FormProvider>
      {renderForgotPasswordDialog}
    </>
  );
}